import { AlertTriangle, ArrowLeft, Copy, Home, Mail, Phone, RefreshCw } from 'lucide-react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

export function ErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();

  let title = 'Algo deu errado';
  let description = 'Ocorreu um erro inesperado. Por favor, tente novamente.';
  let statusCode: number | null = null;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    switch (error.status) {
      case 404:
        title = 'Página não encontrada';
        description = 'A página que você está procurando não existe ou foi movida.';
        break;
      case 401:
        title = 'Não autorizado';
        description = 'Você não tem permissão para acessar esta página.';
        break;
      case 403:
        title = 'Acesso negado';
        description = 'Você não tem permissão para acessar este recurso.';
        break;
      case 500:
        title = 'Erro do servidor';
        description = 'Ocorreu um erro interno. Nossa equipe foi notificada.';
        break;
      default:
        title = `Erro ${error.status}`;
        description = error.statusText || 'Ocorreu um erro inesperado.';
    }
  } else if (error instanceof Error) {
    if (import.meta.env.DEV) {
      description = error.message;
    }
  }

  const getErrorDetails = (): string => {
    const timestamp = new Date().toISOString();
    const url = window.location.href;

    let details = `=== Detalhes do Erro ===\n`;
    details += `Data/Hora: ${timestamp}\n`;
    details += `URL: ${url}\n`;
    details += `Código: ${statusCode || 'N/A'}\n`;
    details += `Título: ${title}\n`;
    details += `Descrição: ${description}\n`;

    if (error instanceof Error) {
      details += `\n=== Erro Técnico ===\n`;
      details += `Mensagem: ${error.message}\n`;
      if (error.stack) {
        details += `\nStack Trace:\n${error.stack}`;
      }
    }

    return details;
  };

  const handleCopyError = async () => {
    try {
      const errorDetails = getErrorDetails();
      await navigator.clipboard.writeText(errorDetails);
      toast({
        title: 'Copiado!',
        description: 'Detalhes do erro copiados para a área de transferência.',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Erro ao copiar',
        description: 'Não foi possível copiar os detalhes do erro. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-10 text-center">
            <div className="inline-flex items-center justify-center h-20 w-20 bg-white/20 backdrop-blur-sm rounded-full mb-4">
              <AlertTriangle className="h-10 w-10 text-white" />
            </div>
            {statusCode && <p className="text-7xl font-bold text-white/30 mb-2">{statusCode}</p>}
            <h1 className="text-2xl font-bold text-white">{title}</h1>
          </div>

          {/* Content */}
          <div className="px-8 py-8 space-y-6">
            {/* Description */}
            <p className="text-gray-600 text-center text-lg">{description}</p>

            {/* Error details in development */}
            {import.meta.env.DEV && error instanceof Error && error.stack && (
              <details className="bg-gray-50 rounded-lg border border-gray-200">
                <summary className="px-4 py-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900 font-medium">
                  Detalhes técnicos (dev)
                </summary>
                <pre className="px-4 pb-4 text-xs text-gray-700 overflow-auto max-h-40 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={handleGoBack} className="h-11">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <Button variant="outline" onClick={handleReload} className="h-11">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Recarregar
                </Button>
              </div>
              <Button onClick={handleGoHome} className="w-full h-11">
                <Home className="h-4 w-4 mr-2" />
                Ir para o início
              </Button>
            </div>
          </div>

          {/* Support Section */}
          <div className="border-t border-gray-100 bg-gray-50 px-8 py-6">
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">
                Se o problema persistir, entre em contato com o suporte:
              </p>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                <a
                  href="mailto:suporte@bstock.com.br"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Mail className="h-4 w-4 mr-1.5" />
                  suporte@bstock.com.br
                </a>
                <span className="hidden sm:inline text-gray-300">|</span>
                <a
                  href="tel:+551199999999"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Phone className="h-4 w-4 mr-1.5" />
                  (11) 9999-9999
                </a>
              </div>

              {/* Copy Error Button */}
              <Button variant="outline" size="sm" onClick={handleCopyError} className="mt-2">
                <Copy className="h-4 w-4 mr-2" />
                Copiar detalhes do erro
              </Button>
              <p className="text-xs text-gray-500">
                Copie os detalhes do erro para enviar ao suporte técnico
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          BStock &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
